import type { FormDialogRef, RadioCard, RadioOption } from '@/components'
import {
  Button,
  FAIcon,
  Field,
  FieldGroup,
  FieldLabel,
  FormDialog,
  Input,
  NumberInput,
  RadioCards,
  SimpleColorPicker,
  Toggles,
} from '@/components'
import type { AbsenceTypeNode } from '@/modules/timesheets/types.graphql'
import { generateRandomColor } from '@/utils'
import { useFormWatch } from '@/utils/hooks/form-hooks'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Activity, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useAbsenceSettingsMutations } from '../../util'
import { AbsencePresetExplanation } from './AbsencePresetExplanation'

function useAbsenceTypeSchema() {
  const translations = useTranslate({
    nameRequired: 'validation.field-required',
    nameMaxLength: ['validation.max-length', { n: 64 }],
    externalIdMaxLength: ['validation.max-length', { n: 64 }],
  })

  return z.object({
    name: z
      .string()
      .trim()
      .min(1, translations.nameRequired)
      .max(64, translations.nameMaxLength()),
    externalId: z.string().trim().max(64, translations.externalIdMaxLength()),
    color: z.string().min(1),
    preset: z.enum([
      'SIMPLE',
      'CUSTOM',
      'VACATION_NO',
      'SELF_CERTIFICATION_NO',
      'SICK_CHILD_NO',
      'FLEXTIME',
    ]),

    interval: z.enum(['YEARLY', 'MONTHLY', 'WEEKLY']),
    resetMode: z.enum(['RUNNING', 'FIRST_DAY']),
    durationMode: z.enum(['DAILY', 'HOURLY']),

    maxHours: z.number().min(0),
    maxHoursContinuous: z.number().min(0),
    maxAbsencesInInterval: z.number().min(0),
  })
}

export type AbsenceTypeForm = z.infer<ReturnType<typeof useAbsenceTypeSchema>>

function transformAbsenceTypeValues(values: AbsenceTypeForm) {
  return {
    maxHours:
      values.durationMode === 'DAILY' ? values.maxHours * 24 : values.maxHours,
    maxHoursContinuous:
      values.durationMode === 'DAILY'
        ? values.maxHoursContinuous * 24
        : values.maxHoursContinuous,
    preset: values.preset === 'CUSTOM' ? null : values.preset,
  }
}

interface Props {
  absenceType?: AbsenceTypeNode
}

export function AbsenceTypeDialog({ absenceType }: Props) {
  const translations = useTranslate({
    addHeader: 'settings.absence.absence-type.add-header',
    editHeader: 'settings.absence.absence-type.edit-header',
    add: 'common.add',

    back: 'settings.absence.absence-type.back',
    next: 'settings.absence.absence-type.next',

    labels: {
      name: 'settings.absence.absence-type.name',
      color: 'settings.absence.absence-type.color',

      externalId: {
        label: 'settings.absence.absence-type.external-id.label',
        description: 'settings.absence.absence-type.external-id.description',
      },

      interval: {
        label: 'settings.absence.absence-type.interval.label',
        description: 'settings.absence.absence-type.interval.description',

        yearly: 'settings.absence.absence-type.interval.yearly',
        monthly: 'settings.absence.absence-type.interval.monthly',
        weekly: 'settings.absence.absence-type.interval.weekly',
      },
      resetMode: {
        label: 'settings.absence.absence-type.reset-mode.label',
        description: 'settings.absence.absence-type.reset-mode.description',

        running: 'settings.absence.absence-type.reset-mode.running',
        firstDay: 'settings.absence.absence-type.reset-mode.first-day',
      },
      durationMode: {
        label: 'settings.absence.absence-type.duration-mode.label',
        description: 'settings.absence.absence-type.duration-mode.description',

        daily: 'settings.absence.absence-type.duration-mode.daily',
        hourly: 'settings.absence.absence-type.duration-mode.hourly',
      },

      maxHours: {
        labelHours: 'settings.absence.absence-type.max-hours.label-hours',
        labelDays: 'settings.absence.absence-type.max-hours.label-days',

        descriptionHoursYear:
          'settings.absence.absence-type.max-hours.description-hours-year',
        descriptionHoursMonth:
          'settings.absence.absence-type.max-hours.description-hours-month',
        descriptionHoursWeek:
          'settings.absence.absence-type.max-hours.description-hours-week',
        descriptionDaysYear:
          'settings.absence.absence-type.max-hours.description-days-year',
        descriptionDaysMonth:
          'settings.absence.absence-type.max-hours.description-days-month',
        descriptionDaysWeek:
          'settings.absence.absence-type.max-hours.description-days-week',
      },
      maxHoursContinuous: {
        labelHours:
          'settings.absence.absence-type.max-hours-continuous.label-hours',
        labelDays:
          'settings.absence.absence-type.max-hours-continuous.label-days',

        descriptionHours:
          'settings.absence.absence-type.max-hours-continuous.description-hours',
        descriptionDays:
          'settings.absence.absence-type.max-hours-continuous.description-days',
      },
      maxAbsencesInInterval: {
        label: 'settings.absence.absence-type.max-absences-in-interval.label',
        description:
          'settings.absence.absence-type.max-absences-in-interval.description',
      },
    },
    presets: {
      simple: {
        title: 'settings.absence.absence-type.presets.simple.title',
        description: 'settings.absence.absence-type.presets.simple.description',
      },
      custom: {
        title: 'settings.absence.absence-type.presets.custom.title',
        description: 'settings.absence.absence-type.presets.custom.description',
      },
      vacationNo: {
        title: 'settings.absence.absence-type.presets.vacation-no.title',
        description:
          'settings.absence.absence-type.presets.vacation-no.description',
      },
      selfCertificationNo: {
        title:
          'settings.absence.absence-type.presets.self-certification-no.title',
        description:
          'settings.absence.absence-type.presets.self-certification-no.description',
      },
      sickChildNo: {
        title: 'settings.absence.absence-type.presets.sick-child-no.title',
        description:
          'settings.absence.absence-type.presets.sick-child-no.description',
      },
    },
  })

  const dialogRef = useRef<FormDialogRef>(null)

  const [page, setPage] = useState<'preset' | 'values'>('preset')

  const form = useForm<AbsenceTypeForm>({
    resolver: zodResolver(useAbsenceTypeSchema()),
    defaultValues: {
      name: absenceType?.name || '',
      externalId: absenceType?.externalId || '',
      color: absenceType?.color || generateRandomColor(),
      preset: absenceType?.preset || 'CUSTOM',

      interval: absenceType?.interval || 'YEARLY',
      resetMode: absenceType?.resetMode || 'RUNNING',
      durationMode: absenceType?.durationMode || 'DAILY',

      maxHours: !absenceType
        ? 0
        : absenceType.durationMode === 'DAILY'
        ? Math.ceil(absenceType.maxHours / 24)
        : absenceType.maxHours,
      maxHoursContinuous: !absenceType
        ? 0
        : absenceType.durationMode === 'DAILY'
        ? Math.ceil(absenceType.maxHoursContinuous / 24)
        : absenceType.maxHoursContinuous,
      maxAbsencesInInterval: absenceType?.maxAbsencesInInterval || 0,
    },
    mode: 'onBlur',
  })

  const presetCards: RadioCard<NonNullable<AbsenceTypeForm['preset']>>[] = [
    {
      value: 'CUSTOM',
      title: translations.presets.custom.title,
      description: translations.presets.custom.description,
    },
    {
      value: 'SIMPLE',
      title: translations.presets.simple.title,
      description: translations.presets.simple.description,
    },
    {
      value: 'VACATION_NO',
      title: translations.presets.vacationNo.title,
      description: translations.presets.vacationNo.description,
    },
    {
      value: 'SELF_CERTIFICATION_NO',
      title: translations.presets.selfCertificationNo.title,
      description: translations.presets.selfCertificationNo.description,
    },
    {
      value: 'SICK_CHILD_NO',
      title: translations.presets.sickChildNo.title,
      description: translations.presets.sickChildNo.description,
    },
  ]

  const intervalOptions: RadioOption<AbsenceTypeForm['interval']>[] = [
    {
      value: 'YEARLY',
      label: translations.labels.interval.yearly,
    },
    {
      value: 'MONTHLY',
      label: translations.labels.interval.monthly,
    },
    {
      value: 'WEEKLY',
      label: translations.labels.interval.weekly,
    },
  ]

  const resetModeOptions: RadioOption<AbsenceTypeForm['resetMode']>[] = [
    {
      value: 'RUNNING',
      label: translations.labels.resetMode.running,
    },
    {
      value: 'FIRST_DAY',
      label: translations.labels.resetMode.firstDay,
    },
  ]

  const durationModeOptions: RadioOption<AbsenceTypeForm['durationMode']>[] = [
    {
      value: 'DAILY',
      label: translations.labels.durationMode.daily,
    },
    {
      value: 'HOURLY',
      label: translations.labels.durationMode.hourly,
    },
  ]

  const name = useFormWatch(form, 'name')
  const nextDisabled = !!form.formState.errors.name || !name.trim()

  const preset = useFormWatch(form, 'preset')
  const durationMode = useFormWatch(form, 'durationMode')
  const interval = useFormWatch(form, 'interval')

  const maxHoursDescription = (() => {
    if (durationMode === 'DAILY') {
      if (interval === 'YEARLY')
        return translations.labels.maxHours.descriptionDaysYear
      else if (interval === 'MONTHLY')
        return translations.labels.maxHours.descriptionDaysMonth
      else return translations.labels.maxHours.descriptionDaysWeek
    } else {
      if (interval === 'YEARLY')
        return translations.labels.maxHours.descriptionHoursYear
      else if (interval === 'MONTHLY')
        return translations.labels.maxHours.descriptionHoursMonth
      else return translations.labels.maxHours.descriptionHoursWeek
    }
  })()

  const mutations = useAbsenceSettingsMutations()

  function resetForm() {
    setTimeout(() => {
      form.reset()
      setPage('preset')
    }, 300)
  }

  async function handleSubmit(values: AbsenceTypeForm) {
    const transformed = transformAbsenceTypeValues(values)

    if (!absenceType)
      await mutations.create({
        variables: {
          input: {
            ...values,
            ...transformed,
          },
        },
      })
    else
      await mutations.patch({
        variables: {
          id: absenceType.id,
          input: {
            ...values,
            ...transformed,
          },
        },
      })

    dialogRef.current?.close()
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      trigger={
        !absenceType ? (
          <Button size="sm">
            <FAIcon icon="plus" />
            {translations.add}
          </Button>
        ) : (
          <div role="button" aria-label="Edit absence type">
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="edit"
              cursor="pointer"
            />
          </div>
        )
      }
      header={!absenceType ? translations.addHeader : translations.editHeader}
      prevButton={{
        show: page === 'values',
        text: translations.back,
        onClick: () => setPage('preset'),
      }}
      nextButton={{
        show: page === 'preset' && !absenceType?.preset && preset !== 'SIMPLE',
        text: translations.next,
        disabled: nextDisabled,
        onClick: () => setPage('values'),
      }}
      noCloseOnEscape
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Activity mode={page === 'preset' ? 'visible' : 'hidden'}>
          <Controller
            name="name"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="absence-name">
                  {translations.labels.name}
                </FieldLabel>

                <Input
                  {...field}
                  id="absence-name"
                  autoComplete="off"
                  maxLength={64}
                  data-1p-ignore
                  error={fieldState.error?.message}
                  aria-invalid={fieldState.invalid}
                />
              </Field>
            )}
          />

          {/* Do not show for FLEXTIME */}
          <Activity
            mode={absenceType?.preset !== 'FLEXTIME' ? 'visible' : 'hidden'}
          >
            <Controller
              name="externalId"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel
                    htmlFor="absence-external-id"
                    info={translations.labels.externalId.description}
                  >
                    {translations.labels.externalId.label}
                  </FieldLabel>

                  <Input
                    {...field}
                    id="absence-external-id"
                    autoComplete="off"
                    maxLength={64}
                    error={fieldState.error?.message}
                    aria-invalid={fieldState.invalid}
                  />
                </Field>
              )}
            />
          </Activity>

          <Controller
            name="color"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="absence-color">
                  {translations.labels.color}
                </FieldLabel>

                <SimpleColorPicker {...field} id="absence-color" />
              </Field>
            )}
          />

          {/* Show preset select if not editing an existing absence type with a preset */}
          <Activity mode={!absenceType?.preset ? 'visible' : 'hidden'}>
            <Controller
              name="preset"
              control={form.control}
              render={({ field, fieldState }) => (
                <RadioCards
                  {...field}
                  options={presetCards}
                  id="absence-preset"
                  aria-invalid={fieldState.invalid}
                />
              )}
            />
          </Activity>

          {/* Show preset explanation if editing an existing absence type with a preset */}
          <Activity
            mode={
              !!absenceType?.preset && absenceType.preset !== 'SIMPLE'
                ? 'visible'
                : 'hidden'
            }
          >
            {preset !== 'CUSTOM' && preset !== 'SIMPLE' && (
              <AbsencePresetExplanation preset={preset} />
            )}
          </Activity>
        </Activity>

        {/* Show custom values if on the values page and the preset is CUSTOM */}
        <Activity
          mode={page === 'values' && preset === 'CUSTOM' ? 'visible' : 'hidden'}
        >
          <Controller
            name="interval"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel info={translations.labels.interval.description}>
                  {translations.labels.interval.label}
                </FieldLabel>

                <Toggles {...field} options={intervalOptions} wide />
              </Field>
            )}
          />

          <Controller
            name="resetMode"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel info={translations.labels.resetMode.description}>
                  {translations.labels.resetMode.label}
                </FieldLabel>

                <Toggles {...field} options={resetModeOptions} wide />
              </Field>
            )}
          />

          <Controller
            name="durationMode"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel info={translations.labels.durationMode.description}>
                  {translations.labels.durationMode.label}
                </FieldLabel>

                <Toggles {...field} options={durationModeOptions} wide />
              </Field>
            )}
          />

          <Controller
            name="maxHours"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <div className="flex items-center justify-between gap-2">
                  <FieldLabel
                    className="whitespace-nowrap"
                    htmlFor="absence-max-hours"
                    info={maxHoursDescription}
                  >
                    {durationMode === 'DAILY'
                      ? translations.labels.maxHours.labelDays
                      : translations.labels.maxHours.labelHours}
                  </FieldLabel>

                  <NumberInput
                    {...field}
                    className="w-min"
                    id="absence-max-hours"
                    size="sm"
                    min={0}
                    aria-invalid={fieldState.invalid}
                  />
                </div>
              </Field>
            )}
          />

          <Controller
            name="maxHoursContinuous"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <div className="flex items-center justify-between gap-2">
                  <FieldLabel
                    className="whitespace-nowrap"
                    htmlFor="absence-max-hours-continuous"
                    info={
                      durationMode === 'DAILY'
                        ? translations.labels.maxHoursContinuous.descriptionDays
                        : translations.labels.maxHoursContinuous
                            .descriptionHours
                    }
                  >
                    {durationMode === 'DAILY'
                      ? translations.labels.maxHoursContinuous.labelDays
                      : translations.labels.maxHoursContinuous.labelHours}
                  </FieldLabel>

                  <NumberInput
                    {...field}
                    className="w-min"
                    id="absence-max-hours-continuous"
                    size="sm"
                    min={0}
                    aria-invalid={fieldState.invalid}
                  />
                </div>
              </Field>
            )}
          />

          <Controller
            name="maxAbsencesInInterval"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <div className="flex items-center justify-between gap-2">
                  <FieldLabel
                    className="whitespace-nowrap"
                    htmlFor="absence-max-absences-in-interval"
                    info={translations.labels.maxAbsencesInInterval.description}
                  >
                    {translations.labels.maxAbsencesInInterval.label}
                  </FieldLabel>

                  <NumberInput
                    {...field}
                    className="w-min"
                    id="absence-max-absences-in-interval"
                    size="sm"
                    min={0}
                    aria-invalid={fieldState.invalid}
                  />
                </div>
              </Field>
            )}
          />
        </Activity>

        {/* Show preset explanation if on the values page and the preset is not CUSTOM or SIMPLE */}
        <Activity
          mode={
            page === 'values' && preset !== 'CUSTOM' && preset !== 'SIMPLE'
              ? 'visible'
              : 'hidden'
          }
        >
          {preset !== 'CUSTOM' && preset !== 'SIMPLE' && (
            <AbsencePresetExplanation preset={preset} />
          )}
        </Activity>
      </FieldGroup>
    </FormDialog>
  )
}
